import React, { useState, useMemo } from "react";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import MasterComponent from "../../../components/Forms";
import { constants } from "../../../constants/common";
import { ComboConfig, MasterConfigs, FormColumnConfig } from "../../../interfaces/entity";
import moment from "moment";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { Button, Flex, Tag, message, Modal, Card, Badge, Typography } from "antd";
import DrawerComponent from "../../../components/Forms/Drawer";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import TasksDetails from "./TasksDetails";
import { useSelector, useDispatch } from "react-redux";
import { postData } from "../../../actions/developer";
const { Text, Title } = Typography;

const TaskManagement: React.FC = () => {
    const [leads, setLeads] = React.useState<string>(null);
    const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
    const [selectedTask, setSelectedTask] = React.useState<any>(null);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [summary, setSummary] = useState<any>(null);
    const dispatch = useDispatch();
    const { user } = useSelector((state: any) => state.app);
    const isAdmin = user?.designation === 'Admin';

    const handleCreateInterviewFeedbackTasks = async () => {
        try {
            setLoadingTasks(true);
            const response = await dispatch(postData('/tasks-management/admin/create-interview-feedback-tasks', {}));

            if (response && response.code === 200 && response.data) {
                const result = response.data;
                Modal.success({
                    title: 'Tasks Created Successfully',
                    content: (
                        <div>
                            <p><strong>Total Interviews Checked:</strong> {result.totalInterviewsChecked || 0}</p>
                            <p><strong>Tasks Created:</strong> {result.tasksCreated || 0}</p>
                            <p><strong>Already Have Feedback:</strong> {result.tasksWithFeedback || 0}</p>
                            <p><strong>Already Have Active Tasks:</strong> {result.tasksAlreadyExist || 0}</p>
                            <p><strong>Skipped:</strong> {result.tasksSkipped || 0}</p>
                            {result.errors && result.errors.length > 0 && (
                                <p style={{ color: 'red' }}><strong>Errors:</strong> {result.errors.length}</p>
                            )}
                        </div>
                    ),
                    width: 500,
                });
                // Refresh the table
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true,
                });
            } else {
                message.error(response?.data?.message || 'Failed to create tasks');
            }
        } catch (error) {
            console.error('Error creating interview feedback tasks:', error);
            message.error('Failed to create interview feedback tasks');
        } finally {
            setLoadingTasks(false);
        }
    };

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/tasks-management`,
        title: "Task Management",
        modalTitle: (e) => `${e} Task`,
        isActionTab: true,
        queryParams: {
            andOr: 'and',
        },
        onDataLoad: (data) => {
            setLeads(data?.refType || null);
        },
        onSummaryLoad: (summaryData: any) => {
            setSummary(summaryData);
        },
        renderSummary: () => {
            if (!summary) return null;

            return (
                <div style={{ width: '100%', marginBottom: 16 }}>
                    <Flex gap={12} wrap="wrap" style={{ width: '100%' }}>
                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #1890ff', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', flexWrap: 'wrap', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Task Status:
                                </Text>
                                <Flex gap={12} align="center" wrap="wrap">
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.total || 0}
                                            title="Total Tasks"
                                            style={{ backgroundColor: '#1890ff' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#1890ff', lineHeight: '30px', margin: 0 }}>T</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.byStatus?.find((s: any) => s.status === 'Completed')?.count || 0}
                                            title="Completed"
                                            style={{ backgroundColor: '#52c41a' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#52c41a', lineHeight: '30px', margin: 0 }}>C</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.byStatus?.find((s: any) => s.status === 'In Progress')?.count || 0}
                                            title="In Progress"
                                            style={{ backgroundColor: '#faad14' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#faad14', lineHeight: '30px', margin: 0 }}>P</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.byStatus?.find((s: any) => s.status === 'Not Started')?.count || 0}
                                            title="Not Started"
                                            style={{ backgroundColor: '#722ed1' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#722ed1', lineHeight: '30px', margin: 0 }}>N</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #13c2c2', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', flexWrap: 'wrap', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Priority:
                                </Text>
                                <Flex gap={12} align="center" wrap="wrap">
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={(summary.byPriority?.find((p: any) => p.priority === 'High')?.count || 0) + (summary.byPriority?.find((p: any) => p.priority === 'Highest')?.count || 0)}
                                            title="High Priority"
                                            style={{ backgroundColor: '#ff4d4f' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#ff4d4f', lineHeight: '30px', margin: 0 }}>H</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.byPriority?.find((p: any) => p.priority === 'Normal')?.count || 0}
                                            title="Normal Priority"
                                            style={{ backgroundColor: '#13c2c2' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#13c2c2', lineHeight: '30px', margin: 0 }}>N</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #722ed1', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', flexWrap: 'wrap', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Date Range:
                                </Text>
                                <Flex gap={12} align="center" wrap="wrap">
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.today || 0}
                                            title="Today"
                                            style={{ backgroundColor: '#722ed1' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#722ed1', lineHeight: '30px', margin: 0 }}>T</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.lastWeek || 0}
                                            title="Last Week"
                                            style={{ backgroundColor: '#eb2f96' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#eb2f96', lineHeight: '30px', margin: 0 }}>W</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.lastMonth || 0}
                                            title="Last Month"
                                            style={{ backgroundColor: '#2f54eb' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#2f54eb', lineHeight: '30px', margin: 0 }}>M</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Card>
                    </Flex>
                </div>
            );
        },
        getFilterColumns: () => {
            const filterColumns: FormColumnConfig[] = [
                {
                    label: 'Task Status',
                    name: 'taskStatus',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Task Status',
                        showSearch: true,
                    },
                    children: constants.TASK_STATUS.map(status => ({
                        name: status,
                        value: status
                    }))
                },
                {
                    label: 'Priority',
                    name: 'priority',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Priority',
                        showSearch: true,
                    },
                    children: constants.PRIORITY.map(priority => ({
                        name: priority,
                        value: priority
                    }))
                },
                {
                    label: 'Task For',
                    name: 'refType',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Task For',
                        showSearch: true,
                    },
                    children: constants.TASK_FOR.map(type => ({
                        name: type,
                        value: type
                    }))
                },
            ];

            // Task Owner filter - only visible to Admin
            if (isAdmin) {
                filterColumns.push({
                    label: 'Task Owner',
                    name: 'taskOwner',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Task Owner',
                        showSearch: true,
                    },
                });
            }

            filterColumns.push(
                {
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 4,
                },
                {
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 4,
                }
            );

            return filterColumns;
        },
        extraControles: isAdmin ? [
            <Button
                key="create-interview-tasks"
                type="primary"
                icon={<ReloadOutlined />}
                loading={loadingTasks}
                onClick={handleCreateInterviewFeedbackTasks}
            >
                Create Auto Tasks for Interview Feedback
            </Button>
        ] : [],
        getColumns: () => [
            {
                title: "Subject", dataIndex: "subject", key: "subject",
                render: (text, record) => <a onClick={() => {
                    setSelectedTask(record);
                    setOpenDrawer(true);
                }}>{text}</a>
            },
            {
                title: "Due Date", dataIndex: "dueDate", key: "dueDate", render: (text) => {
                    if (!text) return '-';
                    const dueDate = moment(text);
                    const now = moment();
                    let status = 'Overdue';
                    let color = '#ff4d4f';
                    let bgColor = '#fff1f0';
                    let borderColor = '#ffccc7';

                    if (dueDate.isAfter(now, 'day')) {
                        status = 'Upcoming';
                        color = '#52c41a';
                        bgColor = '#f6ffed';
                        borderColor = '#b7eb8f';
                    } else if (dueDate.isSame(now, 'day')) {
                        status = 'Today';
                        color = '#1890ff';
                        bgColor = '#e6f7ff';
                        borderColor = '#91d5ff';
                    }

                    return (
                        <Flex align="center" gap={8}>
                            <span>{dueDate.format('DD MMMM YYYY')}</span>
                            <Tag
                                color={color}
                                style={{
                                    backgroundColor: bgColor,
                                    borderColor: borderColor,
                                    color: color,
                                    fontWeight: 500,
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    margin: 0,
                                    border: `1px solid ${borderColor}`
                                }}
                            >
                                {status}
                            </Tag>
                        </Flex>
                    );
                }
            },
            {
                title: "Task Status",
                dataIndex: "taskStatus",
                key: "taskStatus",
                render: (status: string) => {
                    if (!status) return '-';
                    const statusConfig: Record<string, { color: string; bgColor: string; borderColor: string }> = {
                        'Not Started': { color: '#722ed1', bgColor: '#f9f0ff', borderColor: '#d3adf7' },
                        'In Progress': { color: '#faad14', bgColor: '#fffbe6', borderColor: '#ffe58f' },
                        'Completed': { color: '#52c41a', bgColor: '#f6ffed', borderColor: '#b7eb8f' },
                        'On Hold': { color: '#fa8c16', bgColor: '#fff7e6', borderColor: '#ffd591' },
                        'Cancelled': { color: '#ff4d4f', bgColor: '#fff1f0', borderColor: '#ffccc7' },
                        'Deferred': { color: '#8c8c8c', bgColor: '#fafafa', borderColor: '#d9d9d9' },
                        'Waiting for Response': { color: '#1890ff', bgColor: '#e6f7ff', borderColor: '#91d5ff' },
                    };

                    const config = statusConfig[status] || { color: '#8c8c8c', bgColor: '#fafafa', borderColor: '#d9d9d9' };

                    return (
                        <Tag
                            color={config.color}
                            style={{
                                backgroundColor: config.bgColor,
                                borderColor: config.borderColor,
                                color: config.color,
                                fontWeight: 500,
                                borderRadius: '4px',
                                padding: '2px 8px',
                                margin: 0,
                                border: `1px solid ${config.borderColor}`
                            }}
                        >
                            {status}
                        </Tag>
                    );
                }
            },
            {
                title: "Priority",
                dataIndex: "priority",
                key: "priority",
                render: (priority: string) => {
                    if (!priority) return '-';
                    const priorityConfig: Record<string, { color: string; bgColor: string; borderColor: string }> = {
                        'Highest': { color: '#ff4d4f', bgColor: '#fff1f0', borderColor: '#ffccc7' },
                        'High': { color: '#ff7875', bgColor: '#fff2f0', borderColor: '#ffccc7' },
                        'Normal': { color: '#1890ff', bgColor: '#e6f7ff', borderColor: '#91d5ff' },
                        'Low': { color: '#52c41a', bgColor: '#f6ffed', borderColor: '#b7eb8f' },
                        'Lowest': { color: '#8c8c8c', bgColor: '#fafafa', borderColor: '#d9d9d9' },
                    };

                    const config = priorityConfig[priority] || { color: '#8c8c8c', bgColor: '#fafafa', borderColor: '#d9d9d9' };

                    return (
                        <Tag
                            color={config.color}
                            style={{
                                backgroundColor: config.bgColor,
                                borderColor: config.borderColor,
                                color: config.color,
                                fontWeight: 500,
                                borderRadius: '4px',
                                padding: '2px 8px',
                                margin: 0,
                                border: `1px solid ${config.borderColor}`
                            }}
                        >
                            {priority}
                        </Tag>
                    );
                }
            },
            {
                title: "Related To", dataIndex: "refId", key: "refId",
                render: (text, record) => {
                    if (['Leads', 'Contacts', 'User', 'ShortlistedCandidatesForInterview'].includes(record.refType)) {
                        return <SmallProfileCard
                            name={constants.GET_FULL_NAME(text)}
                            avatar={text?.profilePhoto || text?.avatar}
                        />
                    } else if (record.refType === 'Businesses') {
                        return record?.businessName || 'N/A';
                    } else if (record.refType === 'Deals') {
                        return record?.dealName || 'N/A';
                    } else if (record.refType === 'ContractInterview') {
                        return text?.interviewTitle || text?.name || 'Interview';
                    }
                }
            },
            {
                title: "Task Owner", dataIndex: "taskOwner", key: "taskOwner", render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                />
            },
            {
                title: "Created At", dataIndex: "createdAt", key: "createdAt", render: (text) => moment(text).format('LLL')
            },
        ],
        getFormColumns: () => [
            {
                label: 'Task Owner',
                name: 'taskOwner',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Task Owner is required' }],
            },
            {
                span: 16,
            },
            {
                label: "Subject", name: "subject", type: "text", span: 16,
                rules: [{ required: true, message: 'Subject is required' }]
            },
            { label: "Due Date", name: "dueDate", type: "date", rules: [{ required: true, message: 'Due Date is required' }] },
            {
                label: 'Task For', name: 'refType', type: 'select', children: constants.TASK_FOR.map(type => ({ name: type, value: type })),
                selectProps: {
                    onChange: (value) => {
                        setLeads(value);
                    }
                },
                rules: [{ required: true, message: 'Task For is required' }]
            },
            {
                label: "Task For ID", name: "refId", type: "select",
                isCombo: true,
                rules: [{ required: true, message: 'Task For ID is required' }],
            },
            { label: "Task Status", name: "taskStatus", type: "select", children: constants.TASK_STATUS.map(status => ({ name: status, value: status })) },
            { label: "Priority", name: "priority", type: "select", children: constants.PRIORITY.map(priority => ({ name: priority, value: priority })) },
            {
                span: 16,
            },
            { label: "Description", name: "description", type: "textarea", span: 16 },
        ],
        combo: [
            {
                api: '/users',
                name: 'taskOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            leads && {
                api: `/tasks-management/ref-types/list?refType=${leads}`,
                name: 'refId',
                idKey: '_id',
                render: (text) => text.name,
                rerender: [leads]
            }
        ].filter(Boolean) as ComboConfig[],
        actions: [
            {
                actionType: "Add",
                name: "Add Task",
            },
            {
                actionType: 'filter',
                name: 'Filter',
            }
        ]
    }), [isAdmin, loadingTasks, leads, summary]);

    const handleEditClick = (task: any) => {
        setSelectedTask(task);
        setOpenDrawer(true);
    }

    return (
        <div>
            <MasterComponent config={config} />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={[selectedTask?.preName, selectedTask?.firstName, selectedTask?.lastName].filter(Boolean).join(' ')}
                            avatarUrl={selectedTask?.profilePhoto ? process.env.FILE_URL + selectedTask?.profilePhoto : null}
                            role={selectedTask?.title || 'Task Details'}
                        />
                        {!selectedTask?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => handleEditClick(selectedTask)} />
                        </div>}
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%', style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <TasksDetails task={selectedTask} />
            </DrawerComponent>
        </div>
    );
};


export default TaskManagement;